import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trophy, Medal, Award, Trash2, CheckCircle2, XCircle, Target, X, Hourglass, Star } from 'lucide-react';
import cloudLayout from 'd3-cloud';
import Tooltip from '../ui/Tooltip';
import styles from './ResultsView.module.css';

// App's coordinated category-accent tokens (index.css --cat-*) — same family used for
// activity-type icons elsewhere — reused here for word clouds / spinner wheels instead
// of an unrelated hardcoded rainbow palette.
const CATEGORY_ACCENT_VARS = [
    'var(--cat-violet)', 'var(--cat-blue)', 'var(--cat-teal)', 'var(--cat-cyan)', 'var(--cat-green)',
    'var(--cat-amber)', 'var(--cat-orange)', 'var(--cat-rose)', 'var(--cat-pink)', 'var(--cat-indigo)',
];

/* Rank 1/2/3 icon (gold/silver/bronze) — falls back to a plain number for
   rank 4+. Used by podiums / leaderboards instead of medal emoji. */
const RankIcon = ({ rank, size = 18 }) => {
    if (rank === 0) return <Trophy size={size} className={styles.rankIconGold} aria-hidden />;
    if (rank === 1) return <Medal size={size} className={styles.rankIconSilver} aria-hidden />;
    if (rank === 2) return <Award size={size} className={styles.rankIconBronze} aria-hidden />;
    return <span className={styles.rankIconNumber}>{rank + 1}</span>;
};

/**
 * ResultsView
 * 
 * Reusable component to render live results for all activity types.
 * Shared between LiveResultsPanel (Presenter) and ParticipantExperience (Participant).
 */
const ResultsView = ({ type, responses, content, userAnswer = null, isParticipantView = false, onDeleteResponse = null, onReact = null, onReply = null, onAdminReply = null, onReplyReact = null, onHighlightQuestion = null, onDeleteReply = null }) => {
    if (!responses || responses.length === 0) {
        return <div className={styles.empty}><Hourglass size={16} aria-hidden /> Waiting for responses…</div>;
    }

    const renderResults = () => {
        switch (type) {
            case 'multiple-choice':
                return <MCResults responses={responses} content={content} userAnswer={userAnswer} />;
            case 'ranking':
                return <RankingResults responses={responses} content={content} />;
            case 'rating':
                return <RatingResults responses={responses} content={content} />;
            case 'word-cloud':
                return <WordCloudResults responses={responses} />;
            case 'open-text':
            case 'audience-qa':
                return <TextResults responses={responses} type={type} content={content} isParticipantView={isParticipantView} onDeleteResponse={onDeleteResponse} onReact={onReact} onReply={onReply} onAdminReply={onAdminReply} onReplyReact={onReplyReact} onHighlightQuestion={onHighlightQuestion} onDeleteReply={onDeleteReply} />;
            case 'quiz':
                return <QuizResults responses={responses} content={content} userAnswer={userAnswer} isParticipantView={isParticipantView} />;
            case 'spinner-wheel':
                return <SpinnerWheelResults responses={responses} content={content} />;
            case 'correct-order':
                return <CorrectOrderResults responses={responses} content={content} />;
            case 'categories':
                return <CategoriesResults responses={responses} content={content} />;
            case 'rating-scale':
                return <RatingScaleResults responses={responses} content={content} />;
            case 'image-choice':
                return <ImageChoiceResults responses={responses} content={content} />;
            case 'slider':
                return <SliderResults responses={responses} content={content} />;
            case 'numeric-input':
                return <NumericInputResults responses={responses} content={content} />;
            case 'click-image':
                return <ClickImageResults responses={responses} content={content} />;
            case 'budget-allocation':
                return <BudgetAllocationResults responses={responses} content={content} />;
            case 'reaction':
                return <ReactionResults responses={responses} content={content} />;
            case 'pair-matching':
                return <PairMatchingResults responses={responses} content={content} />;
            case 'prediction':
                return <PredictionResults responses={responses} content={content} />;
            case 'timeline-order':
                return <TimelineOrderResults responses={responses} content={content} />;
            case 'hotspot':
                return <HotspotResults responses={responses} content={content} />;
            case 'debate':
                return <DebateResults responses={responses} content={content} />;
            case 'guess-number':
                return <GuessNumberResults responses={responses} content={content} />;
            case 'speed-quiz':
                return <SpeedQuizResults responses={responses} content={content} />;
            case 'drawing':
                return <DrawingResults responses={responses} content={content} />;
            case 'scratch-card':
            case 'slot-machine':
                return <ScratchCardResults responses={responses} content={content} userAnswer={userAnswer} />;
            default:
                return (
                    <div className={styles.unsupported}>
                        <span>Results for this activity type are not available.</span>
                        <span className={styles.unsupportedHint}>{responses.length} response{responses.length !== 1 ? 's' : ''} received.</span>
                    </div>
                );
        }
    };

    const themedType = String(type || 'default').replace(/-/g, '_');
    const typeClass = styles[`type_${themedType}`] || styles.type_default;

    return (
        <div className={`${styles.resultsContainer} ${typeClass}`}>
            {renderResults()}
        </div>
    );
};

/* ── Sub-renderers ─────────────────────────────────────────────────────── */

const OPTION_LETTERS = 'ABCDEFGHIJ';

const MCResults = ({ responses, content, userAnswer = null }) => {
    const options = content?.options || [];
    const { tally, total } = useMemo(() => {
        const t = {};
        let tot = 0;
        responses.forEach(r => {
            const sel = r.answer?.selected;
            const arr = Array.isArray(sel) ? sel : (sel != null ? [sel] : []);
            if (arr.length) tot += 1;
            arr.forEach(i => { t[i] = (t[i] || 0) + 1; });
        });
        return { tally: t, total: Math.max(tot, 1) };
    }, [responses]);

    const showCorrect = content?.settings?.showCorrectAnswer;
    const correctIndices = new Set(content?.correctOptions || []);

    const userSelections = useMemo(() => {
        if (!userAnswer) return new Set();
        const sel = userAnswer?.selected;
        const arr = Array.isArray(sel) ? sel : (sel != null ? [sel] : []);
        return new Set(arr);
    }, [userAnswer]);

    const isUserAnswerCorrect = useMemo(() => {
        if (!userAnswer || !showCorrect || userSelections.size === 0) return null;
        if (correctIndices.size === 0) return null;
        const multipleMode = content?.settings?.multipleOptions;
        if (multipleMode) {
            return userSelections.size === correctIndices.size &&
                Array.from(userSelections).every(i => correctIndices.has(i));
        }
        return userSelections.size === 1 && Array.from(userSelections)[0] === Array.from(correctIndices)[0];
    }, [userAnswer, userSelections, correctIndices, showCorrect, content?.settings?.multipleOptions]);

    return (
        <div className={styles.mcChart}>
            {options.map((opt, i) => {
                const count = tally[i] || 0;
                const pct = Math.round((count / total) * 100);
                const isCorrect = correctIndices.has(i);
                const userSelected = userSelections.has(i);
                const isUserWrong = userSelected && isUserAnswerCorrect === false;
                const isCorrectOnly = !userSelected && showCorrect && isCorrect;
                const letter = OPTION_LETTERS[i] || String(i + 1);

                const rowState = isUserWrong ? 'wrong' : userSelected ? 'user' : isCorrectOnly ? 'correct' : 'default';
                const barState = showCorrect && isCorrect ? 'correct' : isUserWrong ? 'wrong' : userSelected && isUserAnswerCorrect === true ? 'user' : isCorrectOnly ? 'correct' : 'default';

                return (
                    <div
                        key={i}
                        className={`${styles.mcOption} ${styles[`mcOption_${rowState}`]}`}
                        role="listitem"
                    >
                        <div className={styles.mcOptionHead} >
                            {/* <span className={styles.mcOptionLetter} aria-hidden>
                                {letter}
                            </span> */}
                            <div className={styles.mcOptionLabelWrap}>
                                <span className={styles.mcOptionText}>{opt || `Option ${i + 1}`}</span>
                                <div className={styles.mcBadges}>
                                    {userSelected && isUserAnswerCorrect === true && (
                                        <span className={styles.mcBadgeCorrect}><CheckCircle2 size={12} aria-hidden /> Correct</span>
                                    )}
                                    {userSelected && isUserAnswerCorrect === false && (
                                        <span className={styles.mcBadgeWrong}><XCircle size={12} aria-hidden /> Incorrect</span>
                                    )}
                                    {userSelected && isUserAnswerCorrect === null && (
                                        <span className={styles.mcBadgeUser}>Your answer</span>
                                    )}
                                    {isCorrectOnly && (
                                        <span className={styles.mcBadgeAnswer}>Correct answer</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={styles.mcOptionBarWrap}>
                            <div className={styles.mcBarTrack}>
                                <div
                                    className={`${styles.mcBarFill} ${styles[`mcBarFill_${barState}`]}`}
                                    style={{ width: `${pct}%` }}
                                >
                                    {pct > 15 && <span className={styles.mcBarPct}>{pct}%</span>}
                                </div>
                            </div>
                            <span className={styles.mcBarCount}>{count}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ScratchCardResults = ({ responses, content, userAnswer = null }) => {
    const options = (content?.options || []).map((o) => String(o || '').trim()).filter(Boolean);
    const { tally, total } = useMemo(() => {
        const t = {};
        let tot = 0;
        responses.forEach(r => {
            const outcome = r.answer?.outcome;
            if (outcome != null) {
                t[outcome] = (t[outcome] || 0) + 1;
                tot += 1;
            }
        });
        return { tally: t, total: Math.max(tot, 1) };
    }, [responses]);

    const userOutcome = userAnswer?.outcome ?? null;

    return (
        <div className={styles.mcChart}>
            {options.map((opt) => {
                const count = tally[opt] || 0;
                const pct = Math.round((count / total) * 100);
                const isMine = userOutcome != null && userOutcome === opt;

                return (
                    <div key={opt} className={`${styles.mcOption} ${isMine ? styles.mcOption_user : ''}`} role="listitem">
                        <div className={styles.mcOptionHead}>
                            <div className={styles.mcOptionLabelWrap}>
                                <span className={styles.mcOptionText}>{opt}</span>
                                {isMine && (
                                    <div className={styles.mcBadges}>
                                        <span className={styles.mcBadgeUser}>Your result</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.mcOptionBarWrap}>
                            <div className={styles.mcBarTrack}>
                                <div
                                    className={`${styles.mcBarFill} ${isMine ? styles.mcBarFill_user : styles.mcBarFill_default}`}
                                    style={{ width: `${pct}%` }}
                                >
                                    {pct > 15 && <span className={styles.mcBarPct}>{pct}%</span>}
                                </div>
                            </div>
                            <span className={styles.mcBarCount}>{count}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const RankingResults = ({ responses, content }) => {
    const options = content?.items || content?.options || [];
    const scores = useMemo(() => {
        const s = new Array(options.length).fill(0);
        const n = options.length;
        responses.forEach((r) => {
            let order = r.answer?.order;
            if (!Array.isArray(order) || !order.length) {
                const ranking = r.answer?.ranking;
                if (Array.isArray(ranking) && ranking.length) {
                    order = ranking.map((opt) => options.indexOf(opt)).filter((i) => i >= 0);
                }
            }
            if (Array.isArray(order) && order.length) {
                order.forEach((idx, pos) => {
                    const weight = n - pos;
                    if (s[idx] != null) s[idx] += weight;
                });
            }
        });
        return s;
    }, [responses, options]);

    const ranked = useMemo(() => (
        options.map((opt, i) => ({
            label: opt || `Item ${i + 1}`,
            score: scores[i] || 0,
        })).sort((a, b) => b.score - a.score)
    ), [options, scores]);
    const maxScore = Math.max(...ranked.map((x) => x.score), 1);
    const totalResponses = responses.length;
    const topThree = ranked.slice(0, 3);

    return (
        <div className={styles.rankingResults}>
            <div className={styles.rankingPodium}>
                {topThree.map((item, i) => (
                    <div key={`${item.label}-${i}`} className={styles.rankingPodiumCard}>
                        <span className={styles.rankingPodiumRank}><RankIcon rank={i} size={22} /></span>
                        <span className={styles.rankingPodiumLabel}>{item.label}</span>
                        <span className={styles.rankingPodiumScore}>{item.score}</span>
                    </div>
                ))}
            </div>
            <div className={styles.rankingList}>
                {ranked.map((item, i) => (
                    <div key={`${item.label}-${i}`} className={styles.rankingListRow}>
                        <span className={styles.rankingListRank}>#{i + 1}</span>
                        <span className={styles.rankingListLabel}>{item.label}</span>
                        <div className={styles.rankingListMeter}>
                            <div
                                className={styles.rankingListFill}
                                style={{ width: `${(item.score / maxScore) * 100}%` }}
                            />
                        </div>
                        <span className={styles.rankingListScore}>{item.score}</span>
                    </div>
                ))}
            </div>
            {totalResponses > 0 && (
                <span className={styles.rankingResultsMeta}>{totalResponses} response{totalResponses !== 1 ? 's' : ''}</span>
            )}
        </div>
    );
};

const RATING_EMOJIS_5 = ['😠', '😕', '😐', '🙂', '😄'];
const RATING_EMOJIS_10 = ['😠', '😕', '😫', '😐', '🙂', '😊', '😄', '😁', '🤩', '💯'];

const RatingResults = ({ responses, content }) => {
    const max = Math.min(Math.max(2, Number(content?.settings?.maxValue) || 5), 10);
    const isEmoji = content?.settings?.ratingType === 'emojis';
    const ratingSymbols = isEmoji
        ? (max <= 5 ? RATING_EMOJIS_5 : RATING_EMOJIS_10).slice(0, max)
        : null;

    const ratings = responses.map(r => r.answer?.rating).filter(Boolean);
    const avg = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : '—';
    const tally = {};
    for (let i = 1; i <= max; i++) tally[i] = 0;
    ratings.forEach(r => { if (tally[r] != null) tally[r]++; });
    const getLabel = (level) => {
        const n = Number(level);
        if (isEmoji && ratingSymbols && n >= 1 && n <= ratingSymbols.length) {
            return ratingSymbols[n - 1];
        }
        return (
            <span className={styles.starGroup} aria-label={`${n} star${n !== 1 ? 's' : ''}`}>
                {Array.from({ length: n }).map((_, i) => (
                    <Star key={i} size={12} className={styles.starIcon} aria-hidden />
                ))}
            </span>
        );
    };

    return (
        <div className={styles.ratingDistribution}>
            <div className={styles.avgBadge}>Average: <strong>{avg}</strong> / {max}</div>
            <div className={styles.ratingDistributionGrid}>
                {Object.entries(tally).reverse().map(([star, cnt]) => {
                    const pct = Math.round((cnt / Math.max(ratings.length, 1)) * 100);
                    return (
                        <div key={star} className={styles.ratingTile}>
                            <span className={styles.ratingTileLabel}>{getLabel(star)}</span>
                            <span className={styles.ratingTileCount}>{cnt}</span>
                            <span className={styles.ratingTilePct}>{pct}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Font-size/weight range the word cloud scales word frequency into. Smaller than a
// typical full-page cloud since this always renders inside an embedded results card.
const CLOUD_MIN_FONT_SIZE = 14;
const CLOUD_MAX_FONT_SIZE = 56;
const CLOUD_MIN_FONT_WEIGHT = 500;
const CLOUD_MAX_FONT_WEIGHT = 800;
const CLOUD_MAX_WORDS = 40;
const CLOUD_FONT_FAMILY = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/* Tracks its own rendered width via ResizeObserver so the word cloud (an SVG layout
   that needs concrete pixel dimensions up front) stays responsive inside any container. */
function useContainerWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width;
            if (w) setWidth(Math.round(w));
        });
        observer.observe(el);
        setWidth(Math.round(el.getBoundingClientRect().width));
        return () => observer.disconnect();
    }, []);

    return [ref, width];
}

const WordCloudResults = ({ responses }) => {
    const [wrapRef, containerWidth] = useContainerWidth();
    const [hovered, setHovered] = useState(null);
    const [placedWords, setPlacedWords] = useState([]);

    const freq = useMemo(() => {
        const f = {};
        responses.forEach(r => {
            const words = r.answer?.words;
            const text = r.answer?.text;
            const tokens = Array.isArray(words)
                ? words
                : typeof text === 'string'
                    ? text.split(/\s+/)
                    : [];
            tokens.forEach(w => {
                const key = String(w || '').trim().toLowerCase();
                if (!key) return;
                f[key] = (f[key] || 0) + 1;
            });
        });
        return f;
    }, [responses]);

    const entries = useMemo(
        () => Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, CLOUD_MAX_WORDS),
        [freq]
    );
    const minOccurrences = Math.min(...entries.map(([, c]) => c), 1);
    const maxOccurrences = Math.max(...entries.map(([, c]) => c), 1);

    // Linear normalization of word frequency into the size/weight range — same approach
    // as https://dev.to/mattlewandowski93/creating-a-word-cloud-in-react-1999
    const sizeOf = useCallback((value) => {
        const range = maxOccurrences - minOccurrences;
        const normalized = range > 0 ? (value - minOccurrences) / range : 1;
        return Math.round(CLOUD_MIN_FONT_SIZE + normalized * (CLOUD_MAX_FONT_SIZE - CLOUD_MIN_FONT_SIZE));
    }, [minOccurrences, maxOccurrences]);

    const weightOf = useCallback((value) => {
        const range = maxOccurrences - minOccurrences;
        const normalized = range > 0 ? (value - minOccurrences) / range : 1;
        return Math.round(CLOUD_MIN_FONT_WEIGHT + normalized * (CLOUD_MAX_FONT_WEIGHT - CLOUD_MIN_FONT_WEIGHT));
    }, [minOccurrences, maxOccurrences]);

    const height = Math.round(Math.min(420, Math.max(220, containerWidth * 0.55)));

    // Drives d3-cloud's placement algorithm directly (rather than through the react-d3-cloud
    // wrapper, whose react-faux-dom + MutationObserver re-render bridge doesn't reliably
    // repaint under React 18) — layout runs off-DOM, and its 'end' callback hands back each
    // word's computed x/y/rotate, which we render ourselves as plain SVG text elements.
    useEffect(() => {
        if (entries.length === 0 || containerWidth === 0) {
            setPlacedWords([]);
            return;
        }
        const data = entries.map(([text, value]) => ({ text, value }));
        const layout = cloudLayout()
            .size([containerWidth, height])
            .words(data)
            .padding(2)
            .rotate(0)
            .font(CLOUD_FONT_FAMILY)
            .fontWeight((d) => weightOf(d.value))
            .fontSize((d) => sizeOf(d.value))
            .random(() => 0.5)
            .on('end', (words) => setPlacedWords(words));
        layout.start();
        return () => layout.stop();
    }, [entries, containerWidth, height, sizeOf, weightOf]);

    if (entries.length === 0) {
        return <div className={styles.empty}><Hourglass size={16} aria-hidden /> Waiting for words…</div>;
    }

    return (
        <div className={styles.cloudWrap} ref={wrapRef}>
            {containerWidth > 0 && (
                <svg width={containerWidth} height={height} viewBox={`0 0 ${containerWidth} ${height}`}>
                    <g transform={`translate(${containerWidth / 2}, ${height / 2})`}>
                        {placedWords.map((word, index) => (
                            <text
                                key={word.text}
                                textAnchor="middle"
                                transform={`translate(${word.x}, ${word.y}) rotate(${word.rotate})`}
                                style={{
                                    fontFamily: CLOUD_FONT_FAMILY,
                                    fontSize: `${word.size}px`,
                                    fontWeight: word.weight,
                                    fill: CATEGORY_ACCENT_VARS[(word.text.length + index * 3) % CATEGORY_ACCENT_VARS.length],
                                    cursor: 'default',
                                }}
                                onMouseOver={() => setHovered({ text: word.text, value: word.value })}
                                onMouseOut={() => setHovered(null)}
                            >
                                {word.text}
                            </text>
                        ))}
                    </g>
                </svg>
            )}
            <div className={styles.cloudHoverCaption} aria-live="polite">
                {hovered ? `${hovered.text} — ${hovered.value} ${hovered.value === 1 ? 'vote' : 'votes'}` : ' '}
            </div>
        </div>
    );
};

const EMOJI_REACTIONS = ['👍', '❤️', '😂', '👏'];
const QA_VOTE_UP = '👍';
const QA_VOTE_DOWN = '👎';

const TextResults = ({ responses, content, type, isParticipantView = false, onDeleteResponse = null, onReact = null, onReply = null, onAdminReply = null, onReplyReact = null, onHighlightQuestion = null, onDeleteReply = null }) => {
    const [replyingToId, setReplyingToId] = useState(null);
    const [replyingToParentIndex, setReplyingToParentIndex] = useState(null);
    const [replyDraft, setReplyDraft] = useState('');
    const [adminReplyingToId, setAdminReplyingToId] = useState(null);
    const [adminReplyDraft, setAdminReplyDraft] = useState('');

    const showNames = content?.settings?.showNames === true;
    const emojiReactionsOn = content?.settings?.emojiReactions === true;
    const isAudienceQa = type === 'audience-qa';
    const downvotesOn = isAudienceQa && content?.settings?.downvotes === true;
    const showLabels = isAudienceQa && content?.settings?.labels === true;
    const repliesOn = isAudienceQa && content?.settings?.replies === true;
    const canDelete = typeof onDeleteResponse === 'function';
    const canReact = isParticipantView && (isAudienceQa ? true : emojiReactionsOn) && typeof onReact === 'function';
    const canReply = isParticipantView && repliesOn && typeof onReply === 'function';
    const canAdminReply = !isParticipantView && isAudienceQa && repliesOn && typeof onAdminReply === 'function';
    const canReplyReact = isParticipantView && isAudienceQa && repliesOn && typeof onReplyReact === 'function';
    const canHighlight = !isParticipantView && isAudienceQa && typeof onHighlightQuestion === 'function';
    const canDeleteReply = !isParticipantView && isAudienceQa && typeof onDeleteReply === 'function';

    const voteEmojis = isAudienceQa ? (downvotesOn ? [QA_VOTE_UP, QA_VOTE_DOWN] : [QA_VOTE_UP]) : EMOJI_REACTIONS;
    const showVotes = isAudienceQa || (emojiReactionsOn || (responses.some(r => Object.keys(r.reactions || {}).length > 0)));

    const handleReplySubmit = (responseId) => {
        const t = replyDraft.trim();
        if (t && onReply) {
            onReply(responseId, t, replyingToParentIndex);
            setReplyingToId(null);
            setReplyingToParentIndex(null);
            setReplyDraft('');
        }
    };

    const handleAdminReplySubmit = (responseId) => {
        const t = adminReplyDraft.trim();
        if (t && onAdminReply) {
            onAdminReply(responseId, t);
            setAdminReplyingToId(null);
            setAdminReplyDraft('');
        }
    };

    const listClass = isAudienceQa ? `${styles.textList} ${styles.qaFeed}` : styles.textList;

    return (
        <div className={listClass}>
            {isAudienceQa && (
                <div className={styles.qaFeedHeader}>
                    <span className={styles.qaFeedHeaderTitle}>Questions &amp; discussion</span>
                    <span className={styles.qaFeedHeaderCount}>{responses.length} question{responses.length !== 1 ? 's' : ''}</span>
                </div>
            )}
            {responses.map((r, i) => {
                const text = r.answer?.text
                    || (Array.isArray(r.answer?.texts) && r.answer.texts.length ? r.answer.texts.join(', ') : null)
                    || (r.answer?.words || []).join(', ')
                    || JSON.stringify(r.answer);
                const displayName = showNames ? (r.participant || 'Anonymous') : 'Anonymous';
                const reactionCounts = r.reactions || {};
                const upvotes = reactionCounts[QA_VOTE_UP] || 0;
                const downvotes = downvotesOn ? (reactionCounts[QA_VOTE_DOWN] || 0) : 0;
                const voteScore = upvotes - downvotes;
                const replies = Array.isArray(r.answer?.replies) ? r.answer.replies : [];
                const isReplying = replyingToId === r.id;
                const isAdminReplying = adminReplyingToId === r.id;
                const isHighlighted = isAudienceQa && r.answer?.highlighted === true;
                const cardClass = isAudienceQa ? (isHighlighted ? `${styles.qaCard} ${styles.qaCardHighlighted}` : styles.qaCard) : styles.textItem;
                return (
                    <article key={r.id != null ? r.id : i} className={cardClass}>
                        {isAudienceQa && showVotes && (
                            <div className={styles.qaVoteColumn}>
                                <button
                                    type="button"
                                    className={styles.qaVoteBtn}
                                    onClick={() => canReact && r.id != null && onReact(r.id, QA_VOTE_UP)}
                                    disabled={!canReact || r.id == null}
                                    aria-label="Upvote"
                                    title="Upvote"
                                >
                                    <span aria-hidden>{QA_VOTE_UP}</span>
                                </button>
                                <span className={styles.qaVoteScore} title={`${upvotes} up, ${downvotes} down`}>{voteScore}</span>
                                <button
                                    type="button"
                                    className={styles.qaVoteBtn}
                                    onClick={() => canReact && r.id != null && onReact(r.id, QA_VOTE_DOWN)}
                                    disabled={!canReact || r.id == null}
                                    aria-label="Downvote"
                                    title="Downvote"
                                >
                                    <span aria-hidden>{QA_VOTE_DOWN}</span>
                                </button>
                            </div>
                        )}
                        <div className={isAudienceQa ? styles.qaCardBody : undefined}>
                            <div className={styles.textItemHeader}>
                                <span className={styles.participantName}>{displayName}</span>
                                {showLabels && r.answer?.label && (
                                    <span className={styles.qaLabelBadge}>{r.answer.label}</span>
                                )}
                                {isHighlighted && <span className={styles.qaHighlightBadge}>Highlighted</span>}
                                <span className={styles.textItemHeaderActions}>
                                    {canHighlight && r.id != null && (
                                        <Tooltip title={isHighlighted ? 'Remove highlight' : 'Highlight question'} placement="top">
                                            <button
                                                type="button"
                                                className={`${styles.qaHighlightBtn} ${isHighlighted ? styles.qaHighlightBtnActive : ''}`}
                                                onClick={() => onHighlightQuestion(r.id, !isHighlighted)}
                                                aria-label={isHighlighted ? 'Remove highlight' : 'Highlight question'}
                                            >
                                                <Star size={14} fill={isHighlighted ? 'currentColor' : 'none'} aria-hidden />
                                            </button>
                                        </Tooltip>
                                    )}
                                    {canDelete && r.id != null && (
                                        <button
                                            type="button"
                                            className={styles.textItemDeleteBtn}
                                            onClick={() => onDeleteResponse(r.id)}
                                            aria-label="Delete this question"
                                            title="Delete question"
                                        >
                                            <Trash2 size={14} aria-hidden />
                                        </button>
                                    )}
                                </span>
                            </div>
                            <p className={styles.textContent}>{text}</p>
                        {repliesOn && replies.length > 0 && (
                            <div className={styles.qaReplies}>
                                {replies.map((rep, ri) => {
                                    const replyReactionsMap = r.answer?.replyReactions && typeof r.answer.replyReactions === 'object' ? r.answer.replyReactions : {};
                                    const replyReactions = replyReactionsMap[ri] ?? replyReactionsMap[String(ri)] ?? {};
                                    const replyUp = replyReactions[QA_VOTE_UP] || 0;
                                    const replyDown = replyReactions[QA_VOTE_DOWN] || 0;
                                    return (
                                        <div
                                            key={ri}
                                            className={rep.parentIndex != null ? styles.qaReplyItemNested : styles.qaReplyItem}
                                        >
                                            <span className={styles.qaReplyAuthor}>
                                                {rep.participant || 'Anonymous'}
                                                {rep.role === 'admin' && <span className={styles.qaReplyRole}> (Moderator)</span>}
                                                :
                                            </span>
                                            <span className={styles.qaReplyText}>{rep.text}</span>
                                            <div className={styles.qaReplyActions}>
                                                {canReply && r.id != null && (
                                                    <button
                                                        type="button"
                                                        className={styles.qaReplyInlineBtn}
                                                        onClick={() => { setReplyingToId(r.id); setReplyingToParentIndex(ri); setReplyDraft(''); }}
                                                    >
                                                        Reply
                                                    </button>
                                                )}
                                                {canDeleteReply && r.id != null && (
                                                    <Tooltip title="Delete this reply" placement="top">
                                                        <button
                                                            type="button"
                                                            className={styles.qaReplyDeleteBtn}
                                                            onClick={() => onDeleteReply(r.id, ri)}
                                                            aria-label="Delete this reply"
                                                        >
                                                            <Trash2 size={13} aria-hidden />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                                {r.id != null && (
                                                    <span className={styles.qaReplyVotes} role="group" aria-label="Like or dislike this reply">
                                                        {canReplyReact ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={styles.qaReplyVoteBtn}
                                                                    onClick={() => onReplyReact(r.id, ri, QA_VOTE_UP)}
                                                                    aria-label={`Like (${replyUp})`}
                                                                    title="Like"
                                                                >
                                                                    <span aria-hidden>{QA_VOTE_UP}</span>
                                                                    {replyUp > 0 && <span className={styles.qaReplyVoteCount}>{replyUp}</span>}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={styles.qaReplyVoteBtn}
                                                                    onClick={() => onReplyReact(r.id, ri, QA_VOTE_DOWN)}
                                                                    aria-label={`Dislike (${replyDown})`}
                                                                    title="Dislike"
                                                                >
                                                                    <span aria-hidden>{QA_VOTE_DOWN}</span>
                                                                    {replyDown > 0 && <span className={styles.qaReplyVoteCount}>{replyDown}</span>}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <span className={styles.qaReplyCountsOnly}>
                                                                <span className={styles.qaReplyVoteCountItem} title="Likes">{QA_VOTE_UP} {replyUp}</span>
                                                                <span className={styles.qaReplyVoteCountItem} title="Dislikes">{QA_VOTE_DOWN} {replyDown}</span>
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {showVotes && !isAudienceQa && (emojiReactionsOn || Object.keys(reactionCounts).length > 0) && (
                            <div className={styles.emojiReactionsRow} role="group" aria-label="Reactions">
                                {voteEmojis.map(emoji => {
                                    const count = reactionCounts[emoji] || 0;
                                    return (
                                        <button
                                            key={emoji}
                                            type="button"
                                            className={styles.emojiReactionBtn}
                                            onClick={() => canReact && r.id != null && onReact(r.id, emoji)}
                                            disabled={!canReact || r.id == null}
                                            aria-label={count > 0 ? `${emoji} ${count}` : `React ${emoji}`}
                                        >
                                            <span aria-hidden>{emoji}</span>
                                            {count > 0 && <span className={styles.emojiCount}>{count}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {canReply && r.id != null && (
                            <>
                                {!isReplying ? (
                                    <button
                                        type="button"
                                        className={styles.qaReplyBtn}
                                        onClick={() => { setReplyingToId(r.id); setReplyingToParentIndex(null); setReplyDraft(''); }}
                                    >
                                        Reply
                                    </button>
                                ) : (
                                    <div className={styles.qaReplyForm}>
                                        <textarea
                                            className={styles.qaReplyInput}
                                            value={replyDraft}
                                            onChange={(e) => setReplyDraft(e.target.value)}
                                            placeholder={replyingToParentIndex != null ? 'Reply to this comment…' : 'Write a reply…'}
                                            rows={2}
                                        />
                                        <div className={styles.qaReplyFormActions}>
                                            <button type="button" className={styles.qaReplyCancelBtn} onClick={() => { setReplyingToId(null); setReplyingToParentIndex(null); setReplyDraft(''); }}>Cancel</button>
                                            <button type="button" className={styles.qaReplySubmitBtn} onClick={() => handleReplySubmit(r.id)} disabled={!replyDraft.trim()}>Send</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {canAdminReply && r.id != null && (
                            <>
                                {!isAdminReplying ? (
                                    <button
                                        type="button"
                                        className={styles.qaCommentBtn}
                                        onClick={() => { setAdminReplyingToId(r.id); setAdminReplyDraft(''); }}
                                    >
                                        Comment
                                    </button>
                                ) : (
                                    <div className={styles.qaReplyForm}>
                                        <textarea
                                            className={styles.qaReplyInput}
                                            value={adminReplyDraft}
                                            onChange={(e) => setAdminReplyDraft(e.target.value)}
                                            placeholder="Add a comment as moderator…"
                                            rows={2}
                                        />
                                        <div className={styles.qaReplyFormActions}>
                                            <button type="button" className={styles.qaReplyCancelBtn} onClick={() => { setAdminReplyingToId(null); setAdminReplyDraft(''); }}>Cancel</button>
                                            <button type="button" className={styles.qaReplySubmitBtn} onClick={() => handleAdminReplySubmit(r.id)} disabled={!adminReplyDraft.trim()}>Send</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        </div>
                    </article>
                );
            })}
        </div>
    );
};

const QuizResults = ({ responses, content, userAnswer = null, isParticipantView = false }) => {
    const questions = content?.questions || [];
    const showLeaderboard = content?.settings?.leaderboard !== false;
    const resultsVisible = content?.settings?.pollResults === 'visible';
    const scored = responses.map(r => {
        const ans = r.answer?.answers || {};
        const score = questions.reduce((acc, q, qi) => {
            return acc + (ans[qi] === q.correct ? 1 : 0);
        }, 0);
        return { participant: r.participant, score, total: questions.length };
    });
    scored.sort((a, b) => b.score - a.score);

    const showReview = isParticipantView && userAnswer && resultsVisible && questions.length > 0;
    const userAnswers = userAnswer?.answers && typeof userAnswer.answers === 'object' ? userAnswer.answers : {};

    return (
        <>
            {showReview && (
                <div className={styles.quizReview}>
                    <h3 className={styles.quizReviewTitle}>Your score</h3>
                    <div className={styles.quizReviewScore}>
                        {Object.keys(userAnswers).length > 0
                            ? questions.reduce((acc, q, qi) => acc + (userAnswers[qi] === q.correct ? 1 : 0), 0)
                            : 0}
                        /{questions.length}
                    </div>
                    <h3 className={styles.quizReviewTitle}>Review</h3>
                    <ul className={styles.quizReviewList}>
                        {questions.map((q, qi) => {
                            const opts = q.options || [];
                            const correctIdx = q.correct;
                            const userIdx = userAnswers[qi];
                            const isCorrect = userIdx === correctIdx;
                            const correctLetter = correctIdx != null ? String.fromCharCode(65 + correctIdx) : '';
                            const correctText = opts[correctIdx];
                            const userLetter = userIdx != null ? String.fromCharCode(65 + userIdx) : '';
                            const userText = userIdx != null ? opts[userIdx] : null;
                            return (
                                <li key={qi} className={styles.quizReviewItem}>
                                    <div className={styles.quizReviewQuestion}>
                                        Q{qi + 1}: {q.text || `Question ${qi + 1}`}
                                    </div>
                                    <div className={isCorrect ? styles.quizReviewCorrect : styles.quizReviewIncorrect}>
                                        {isCorrect ? (
                                            <><CheckCircle2 size={13} aria-hidden /> Your answer ({userLetter}. {userText}) is correct.</>
                                        ) : (
                                            <>
                                                Your answer: {userIdx != null ? `${userLetter}. ${userText || '—'}` : '—'}
                                                {correctIdx != null && (
                                                    <span className={styles.quizReviewCorrectAnswer}>
                                                        Correct answer: {correctLetter}. {correctText || '—'}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            {showLeaderboard ? (
                <div className={styles.leaderboard}>
                    {scored.length >= 3 ? (
                        <>
                            <div className={styles.leaderboardPodium}>
                                {/* Order: 2nd (left), 1st (center), 3rd (right) so 1st is elevated in middle */}
                                <div className={`${styles.podiumCard} ${styles.podium2nd}`}>
                                    <span className={styles.podiumRank}>2nd</span>
                                    <div className={styles.podiumAvatarWrap}>
                                        <span className={styles.podiumMedal}><RankIcon rank={1} size={20} /></span>
                                    </div>
                                    <span className={styles.podiumName}>{scored[1].participant}</span>
                                    <span className={styles.podiumScore}>{scored[1].score}/{scored[1].total}</span>
                                </div>
                                <div className={`${styles.podiumCard} ${styles.podium1st}`}>
                                    <span className={styles.podiumRank}>1st</span>
                                    <Trophy size={28} className={styles.podiumTrophy} aria-hidden />
                                    <div className={styles.podiumAvatarWrap}>
                                        <span className={styles.podiumMedal}><RankIcon rank={0} size={20} /></span>
                                    </div>
                                    <span className={styles.podiumName}>{scored[0].participant}</span>
                                    <span className={styles.podiumScore}>{scored[0].score}/{scored[0].total}</span>
                                </div>
                                <div className={`${styles.podiumCard} ${styles.podium3rd}`}>
                                    <span className={styles.podiumRank}>3rd</span>
                                    <div className={styles.podiumAvatarWrap}>
                                        <span className={styles.podiumMedal}><RankIcon rank={2} size={20} /></span>
                                    </div>
                                    <span className={styles.podiumName}>{scored[2].participant}</span>
                                    <span className={styles.podiumScore}>{scored[2].score}/{scored[2].total}</span>
                                </div>
                            </div>
                            {scored.slice(3).map((s, i) => (
                                <div key={i + 3} className={styles.leaderRow}>
                                    <div className={styles.leaderLeft}>
                                        <span className={styles.leaderRank}>{i + 4}</span>
                                        <span className={styles.leaderName}>{s.participant}</span>
                                    </div>
                                    <div className={styles.leaderMeta}>
                                        <div className={styles.leaderTrack}>
                                            <div
                                                className={styles.leaderFill}
                                                style={{ width: `${(s.score / Math.max(s.total, 1)) * 100}%` }}
                                            />
                                        </div>
                                        <span className={styles.leaderScore}>{s.score}/{s.total}</span>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        scored.map((s, i) => (
                            <div key={i} className={styles.leaderRow}>
                                <div className={styles.leaderLeft}>
                                    <span className={styles.leaderRank}>
                                        <RankIcon rank={i} size={16} />
                                    </span>
                                    <span className={styles.leaderName}>{s.participant}</span>
                                </div>
                                <div className={styles.leaderMeta}>
                                    <div className={styles.leaderTrack}>
                                        <div
                                            className={styles.leaderFill}
                                            style={{ width: `${(s.score / Math.max(s.total, 1)) * 100}%` }}
                                        />
                                    </div>
                                    <span className={styles.leaderScore}>{s.score}/{s.total}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            ) : (
                <div className={styles.quizScoresCompact}>
                    <div className={styles.quizScoresSummary}>
                        {responses.length} response{responses.length !== 1 ? 's' : ''}
                    </div>
                    <ul className={styles.quizScoresList}>
                        {scored.map((s, i) => (
                            <li key={i} className={styles.quizScoreItem}>
                                <span className={styles.leaderName}>{s.participant}</span>
                                <span className={styles.leaderScore}>{s.score}/{s.total}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
};

const buildDonutGradient = (entries, colors) => {
    const total = Math.max(entries.reduce((sum, [, c]) => sum + c, 0), 1);
    let cursor = 0;
    const slices = entries.map(([, count], i) => {
        const size = (count / total) * 100;
        const start = cursor;
        cursor += size;
        return `${colors[i % colors.length]} ${start}% ${cursor}%`;
    });
    return `conic-gradient(${slices.join(', ')})`;
};

const SpinnerWheelResults = ({ responses, content }) => {
    const segments = content?.segments || [];
    const { tally, staleCount } = useMemo(() => {
        const t = {};
        segments.forEach(s => { t[s] = 0; });
        // A segment can be deleted by the host after participants have already spun and
        // landed on it — their stored answer.selected/index no longer matches any current
        // segment. Track how many responses fall into that bucket so we can warn the admin
        // instead of letting them silently drop out of the tally below.
        const segmentSet = new Set(segments);
        let stale = 0;
        responses.forEach(r => {
            const sel = r.answer?.selected ?? r.answer?.spinResult;
            const key = sel != null && sel !== '' ? String(sel) : null;
            if (key) {
                if (t[key] != null) t[key]++;
                else t[key] = 1;
                if (segments.length && !segmentSet.has(key)) stale++;
            }
        });
        const keys = segments.length ? segments : Object.keys(t).sort();
        keys.forEach(k => { if (t[k] == null) t[k] = 0; });
        return { tally: t, staleCount: stale };
    }, [responses, segments]);

    const entries = segments.length
        ? segments.map(s => [s, tally[s] ?? 0])
        : Object.entries(tally).sort((a, b) => b[1] - a[1]);
    const totalSpins = Math.max(responses.length, 1);
    const palette = CATEGORY_ACCENT_VARS;
    const donut = buildDonutGradient(entries.length ? entries : [['No data', 1]], palette);

    return (
        <div className={styles.spinnerResults}>
            {staleCount > 0 && (
                <p className={styles.spinnerStaleWarning}>
                    {staleCount} response{staleCount !== 1 ? 's' : ''} landed on a segment that has since been deleted and {staleCount !== 1 ? "aren't" : "isn't"} counted below.
                </p>
            )}
            <div className={styles.spinnerDonutWrap}>
                <div className={styles.spinnerDonut} style={{ background: donut }}>
                    <div className={styles.spinnerDonutCenter}>
                        <span className={styles.spinnerDonutValue}>{responses.length}</span>
                        <span className={styles.spinnerDonutLabel}>spins</span>
                    </div>
                </div>
            </div>
            <div className={styles.spinnerLegend}>
                {entries.length === 0 ? (
                    <p className={styles.avgBadge}>No segments configured. Add segments in the editor.</p>
                ) : (
                    entries
                        .slice()
                        .sort((a, b) => b[1] - a[1])
                        .map(([seg, cnt], i) => (
                            <div key={seg} className={styles.spinnerLegendRow}>
                                <span className={styles.spinnerLegendDot} style={{ background: palette[i % palette.length] }} />
                                <span className={styles.spinnerLegendLabel}>{seg}</span>
                                <span className={styles.spinnerLegendValue}>{Math.round((cnt / totalSpins) * 100)}%</span>
                            </div>
                        ))
                )}
            </div>
        </div>
    );
};

const CorrectOrderResults = ({ responses, content }) => {
    const correctItems = content?.items || [];
    const scored = responses.map(r => {
        const order = r.answer?.order || [];
        let correct = 0;
        correctItems.forEach((item, i) => {
            if (order[i] === item) correct++;
        });
        return { participant: r.participant, correct, total: correctItems.length };
    });
    scored.sort((a, b) => b.correct - a.correct);

    return (
        <div className={styles.performanceBoard}>
            <div className={styles.avgBadge}>
                Avg accuracy: <strong>
                    {scored.length
                        ? Math.round(scored.reduce((s, r) => s + r.correct, 0) / scored.length / Math.max(scored[0]?.total, 1) * 100)
                        : 0}%
                </strong>
            </div>
            <div className={styles.performanceGrid}>
                {scored.map((s, i) => {
                    const pct = Math.round((s.correct / Math.max(s.total, 1)) * 100);
                    return (
                        <div key={i} className={styles.performanceCard}>
                            <div
                                className={styles.performanceRing}
                                style={{ background: `conic-gradient(var(--rv-accent-1) ${pct}%, var(--bg-tertiary) 0)` }}
                            >
                                <div className={styles.performanceRingInner}>{pct}%</div>
                            </div>
                            <span className={styles.performanceName}>{s.participant}</span>
                            <span className={styles.performanceMeta}>{s.correct}/{s.total}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CategoriesResults = ({ responses, content }) => {
    const categories = content?.categories || [];
    const correctMap = {};
    categories.forEach(cat => {
        (cat.items || [])
            .filter(item => item != null && String(item).trim() !== '')
            .forEach(item => { correctMap[String(item).trim()] = cat.name; });
    });
    const totalItems = Object.keys(correctMap).length;

    const scored = responses.map(r => {
        const assignments = r.answer?.assignments || {};
        let correct = 0;
        Object.entries(assignments).forEach(([item, cat]) => {
            const key = item != null ? String(item).trim() : '';
            if (key && correctMap[key] === cat) correct++;
        });
        return { participant: r.participant, correct, total: totalItems };
    });
    scored.sort((a, b) => b.correct - a.correct);

    return (
        <div className={styles.categoriesResults}>
            <div className={styles.avgBadge}>
                Avg accuracy: <strong>
                    {scored.length
                        ? Math.round(scored.reduce((s, r) => s + r.correct, 0) / scored.length / Math.max(totalItems, 1) * 100)
                        : 0}%
                </strong>
            </div>
            <div className={styles.categoriesRows}>
                {scored.map((s, i) => {
                    const pct = Math.round((s.correct / Math.max(s.total, 1)) * 100);
                    return (
                        <div key={i} className={styles.categoriesRow}>
                            <span className={styles.categoriesName}>{s.participant}</span>
                            <div className={styles.categoriesMeter}>
                                <div
                                    className={styles.categoriesFill}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <span className={styles.categoriesMeta}>{s.correct}/{s.total}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const RatingScaleResults = ({ responses, content }) => {
    const minVal = Math.max(0, Number(content?.settings?.minValue) ?? 1);
    const maxValRaw = Math.min(100, Number(content?.settings?.maxValue) ?? 10);
    const min = minVal;
    const max = maxValRaw > minVal ? maxValRaw : minVal + 1;
    const ratings = responses.map(r => r.answer?.rating).filter(r => r != null);
    const avg = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : '—';
    const tally = {};
    for (let i = min; i <= max; i++) tally[i] = 0;
    ratings.forEach(r => { if (tally[r] != null) tally[r]++; });
    const maxCount = Math.max(...Object.values(tally), 1);

    return (
        <div className={styles.scaleResults}>
            <div className={styles.avgBadge}>Average: <strong>{avg}</strong> / {max}</div>
            <div className={styles.scaleHistogram}>
                {Object.entries(tally).map(([val, cnt]) => (
                    <div key={val} className={styles.scaleCol}>
                        <div
                            className={styles.scaleColBar}
                            style={{ height: `${(cnt / maxCount) * 100}%` }}
                            title={`${val}: ${cnt}`}
                        />
                        <span className={styles.scaleColLabel}>{val}</span>
                        <span className={styles.scaleColCount}>{cnt}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Image Choice ──────────────────────────────────────────────────────────────
const ImageChoiceResults = ({ responses, content }) => {
    const options = content?.options || [];
    const tally = useMemo(() => {
        const t = {};
        options.forEach(o => { t[o.id] = 0; });
        responses.forEach(r => {
            (r.answer?.selectedOptions || []).forEach(id => { t[id] = (t[id] || 0) + 1; });
        });
        return t;
    }, [responses, options]);
    const total = Math.max(responses.length, 1);
    const maxCount = Math.max(...Object.values(tally), 1);

    return (
        <div className={styles.mcChart}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                {options.map(opt => {
                    const count = tally[opt.id] || 0;
                    const pct = Math.round((count / total) * 100);
                    const isLeader = count === maxCount && count > 0;
                    return (
                        <div key={opt.id} style={{ border: `2px solid ${isLeader ? 'var(--primary-500)' : 'var(--border)'}`, borderRadius: 'var(--radius-xl)', overflow: 'hidden', background: 'var(--bg-secondary)', transition: 'border-color 0.2s' }}>
                            <img src={opt.imageUrl} alt={opt.label || opt.id} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                            <div style={{ padding: 'var(--space-2)', textAlign: 'center' }}>
                                {opt.label && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 4 }}>{opt.label}</div>}
                                <div style={{ fontWeight: 'var(--font-bold)', color: isLeader ? 'var(--primary-500)' : 'var(--text-primary)' }}>{count} <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'normal', color: 'var(--text-muted)' }}>({pct}%)</span></div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <span className={styles.rankingResultsMeta}>{responses.length} response{responses.length !== 1 ? 's' : ''}</span>
        </div>
    );
};

// ── Slider ────────────────────────────────────────────────────────────────────
const SliderResults = ({ responses, content }) => {
    const min = Number(content?.settings?.min ?? 0);
    const max = Number(content?.settings?.max ?? 100);
    const step = Number(content?.settings?.step ?? 1);
    const values = responses.map(r => r.answer?.value).filter(v => typeof v === 'number');
    const avg = values.length ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1) : '—';
    const minVal = values.length ? Math.min(...values) : '—';
    const maxVal = values.length ? Math.max(...values) : '—';

    const distribution = {};
    for (let v = min; v <= max; v += step) distribution[String(v)] = 0;
    values.forEach(v => { const k = String(v); if (distribution[k] !== undefined) distribution[k]++; });
    const maxCount = Math.max(...Object.values(distribution), 1);
    const entries = Object.entries(distribution);

    return (
        <div className={styles.scaleResults}>
            <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
                {[['Average', avg], ['Min', minVal], ['Max', maxVal]].map(([label, val]) => (
                    <div key={label} className={styles.avgBadge} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{label}</span>
                        <strong>{val}</strong>
                    </div>
                ))}
            </div>
            <div className={styles.scaleHistogram}>
                {entries.map(([val, cnt]) => (
                    <div key={val} className={styles.scaleCol}>
                        <div className={styles.scaleColBar} style={{ height: `${(cnt / maxCount) * 100}%` }} title={`${val}: ${cnt}`} />
                        <span className={styles.scaleColLabel}>{val}</span>
                    </div>
                ))}
            </div>
            <span className={styles.rankingResultsMeta}>{responses.length} response{responses.length !== 1 ? 's' : ''}</span>
        </div>
    );
};

// ── Numeric Input ─────────────────────────────────────────────────────────────
const NumericInputResults = ({ responses }) => {
    const values = useMemo(() => {
        const v = responses.map(r => r.answer?.value).filter(v => typeof v === 'number').sort((a, b) => a - b);
        return v;
    }, [responses]);

    const avg = values.length ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : '—';
    const mid = Math.floor(values.length / 2);
    const median = values.length === 0 ? '—' : values.length % 2 === 0 ? ((values[mid - 1] + values[mid]) / 2).toFixed(1) : values[mid];

    const distribution = {};
    values.forEach(v => { const k = String(v); distribution[k] = (distribution[k] || 0) + 1; });
    const entries = Object.entries(distribution).sort((a, b) => Number(a[0]) - Number(b[0]));
    const maxCount = Math.max(...entries.map(([, c]) => c), 1);

    return (
        <div className={styles.scaleResults}>
            <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
                {[['Average', avg], ['Median', median], ['Responses', values.length]].map(([label, val]) => (
                    <div key={label} className={styles.avgBadge} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{label}</span>
                        <strong>{val}</strong>
                    </div>
                ))}
            </div>
            {entries.length > 0 && (
                <div className={styles.scaleHistogram}>
                    {entries.map(([val, cnt]) => (
                        <div key={val} className={styles.scaleCol}>
                            <div className={styles.scaleColBar} style={{ height: `${(cnt / maxCount) * 100}%` }} title={`${val}: ${cnt}`} />
                            <span className={styles.scaleColLabel}>{val}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Click on Image ────────────────────────────────────────────────────────────
const DEFAULT_CLICK_MARKER_COLOR = '#0ea5e9';
const hexToRgbaClickImage = (hex, alpha) => {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return `rgba(14,165,233,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const ClickImageResults = ({ responses, content }) => {
    const imageUrl = content?.settings?.imageUrl || '';
    const imageNaturalWidth = content?.settings?.imageNaturalWidth;
    const imageNaturalHeight = content?.settings?.imageNaturalHeight;
    const hasAspectRatio = imageNaturalWidth && imageNaturalHeight;
    const markerColor = content?.settings?.clickMarkerColor || DEFAULT_CLICK_MARKER_COLOR;
    const allClicks = useMemo(() => {
        const clicks = [];
        responses.forEach(r => { (r.answer?.clicks || []).forEach(c => clicks.push(c)); });
        return clicks;
    }, [responses]);

    return (
        <div>
            <div className={styles.avgBadge} style={{ marginBottom: 'var(--space-3)' }}>
                {allClicks.length} click{allClicks.length !== 1 ? 's' : ''} from {responses.length} response{responses.length !== 1 ? 's' : ''}
            </div>
            <div
                style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    ...(hasAspectRatio ? { aspectRatio: `${imageNaturalWidth} / ${imageNaturalHeight}` } : {}),
                }}
            >
                <img
                    src={imageUrl}
                    alt="Click heatmap"
                    style={{
                        width: '100%',
                        height: hasAspectRatio ? '100%' : undefined,
                        display: 'block',
                        maxHeight: hasAspectRatio ? undefined : 360,
                        objectFit: 'contain',
                        background: 'var(--bg-tertiary)',
                    }}
                />
                {allClicks.map((c, i) => (
                    <div
                        key={i}
                        style={{
                            position: 'absolute',
                            left: `${c.x}%`,
                            top: `${c.y}%`,
                            transform: 'translate(-50%,-50%)',
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            background: hexToRgbaClickImage(markerColor, 0.85),
                            border: `2px solid ${markerColor}`,
                            boxShadow: `0 0 6px ${hexToRgbaClickImage(markerColor, 0.7)}`,
                            pointerEvents: 'none',
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

// ── Budget Allocation ─────────────────────────────────────────────────────────
const BudgetAllocationResults = ({ responses, content }) => {
    const options = content?.options || [];
    const totals = useMemo(() => {
        const t = {};
        options.forEach(o => { t[o.id] = 0; });
        responses.forEach(r => {
            (r.answer?.allocations || []).forEach(a => { t[a.optionId] = (t[a.optionId] || 0) + Number(a.points || 0); });
        });
        return t;
    }, [responses, options]);

    const maxVal = Math.max(...Object.values(totals), 1);

    return (
        <div className={styles.mcChart}>
            {options.map(opt => {
                const pts = totals[opt.id] || 0;
                const pct = Math.round((pts / maxVal) * 100);
                return (
                    <div key={opt.id} className={styles.mcOption}>
                        <div className={styles.mcOptionHead}>
                            <div className={styles.mcOptionLabelWrap}>
                                <span className={styles.mcOptionText}>{opt.label || opt.id}</span>
                            </div>
                        </div>
                        <div className={styles.mcOptionBarWrap}>
                            <div className={styles.mcBarTrack}>
                                <div className={`${styles.mcBarFill} ${styles.mcBarFill_default}`} style={{ width: `${pct}%` }}>
                                    {pct > 15 && <span className={styles.mcBarPct}>{pct}%</span>}
                                </div>
                            </div>
                            <span className={styles.mcBarCount}>{pts} pts</span>
                        </div>
                    </div>
                );
            })}
            <span className={styles.rankingResultsMeta}>{responses.length} response{responses.length !== 1 ? 's' : ''}</span>
        </div>
    );
};

// ── Reaction ──────────────────────────────────────────────────────────────────
const ReactionResults = ({ responses, content }) => {
    const DEFAULT_REACTIONS = ['👍', '❤️', '😂', '😮', '👎'];
    const options = content?.options?.length ? content.options : DEFAULT_REACTIONS;
    const counts = useMemo(() => {
        const c = {};
        options.forEach(r => { c[r] = 0; });
        responses.forEach(r => {
            const rx = r.answer?.reaction;
            if (rx) c[rx] = (c[rx] || 0) + 1;
        });
        return c;
    }, [responses, options]);
    const total = Math.max(responses.length, 1);
    const maxCount = Math.max(...Object.values(counts), 1);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div className={styles.avgBadge}>{responses.length} reaction{responses.length !== 1 ? 's' : ''}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', justifyContent: 'center' }}>
                {options.map(emoji => {
                    const count = counts[emoji] || 0;
                    const pct = Math.round((count / total) * 100);
                    const scale = 1 + (count / maxCount) * 0.5;
                    return (
                        <div key={emoji} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: `${scale * 2}rem`, transition: 'font-size 0.3s ease' }}>{emoji}</span>
                            <span style={{ fontWeight: 'var(--font-bold)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>{count}</span>
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{pct}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Pair Matching ─────────────────────────────────────────────────────────────
const PairMatchingResults = ({ responses, content }) => {
    const pairs = content?.pairs || [];
    const correctMap = useMemo(() => {
        const m = {};
        pairs.forEach(p => { m[p.left] = p.right; });
        return m;
    }, [pairs]);

    const scored = useMemo(() => responses.map(r => {
        const matches = r.answer?.matches || [];
        let correct = 0;
        matches.forEach(m => { if (correctMap[m.left] === m.right) correct++; });
        return { participant: r.participant, correct, total: pairs.length };
    }).sort((a, b) => b.correct - a.correct), [responses, correctMap, pairs]);

    const avg = scored.length ? scored.reduce((s, r) => s + r.correct, 0) / scored.length / Math.max(pairs.length, 1) * 100 : 0;

    return (
        <div className={styles.performanceBoard}>
            <div className={styles.avgBadge}>Avg accuracy: <strong>{avg.toFixed(0)}%</strong></div>
            <div className={styles.categoriesRows}>
                {scored.map((s, i) => {
                    const pct = Math.round((s.correct / Math.max(s.total, 1)) * 100);
                    return (
                        <div key={i} className={styles.categoriesRow}>
                            <span className={styles.categoriesName}>{s.participant}</span>
                            <div className={styles.categoriesMeter}>
                                <div className={styles.categoriesFill} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={styles.categoriesMeta}>{s.correct}/{s.total}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Prediction ────────────────────────────────────────────────────────────────
const PredictionResults = ({ responses, content }) => {
    const options = content?.options || [];
    const outcome = content?.settings?.outcome;
    const tally = useMemo(() => {
        const t = {};
        responses.forEach(r => {
            const p = r.answer?.prediction;
            if (p) t[p] = (t[p] || 0) + 1;
        });
        return t;
    }, [responses]);
    const total = Math.max(responses.length, 1);

    return (
        <div className={styles.mcChart}>
            {outcome && (
                <div
                    className={styles.avgBadge}
                    style={{
                        marginBottom: 'var(--space-3)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                        borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)',
                    }}
                >
                    <CheckCircle2 size={14} aria-hidden /> Outcome: <strong>{options.find(o => (o.id || o) === outcome)?.label || outcome}</strong>
                    <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                        — {responses.filter(r => r.answer?.prediction === outcome).length} correct prediction{responses.filter(r => r.answer?.prediction === outcome).length !== 1 ? 's' : ''}
                    </span>
                </div>
            )}
            {options.map(opt => {
                const id = opt.id || opt;
                const label = opt.label || opt;
                const count = tally[id] || 0;
                const pct = Math.round((count / total) * 100);
                const isCorrect = outcome && id === outcome;
                return (
                    <div key={id} className={`${styles.mcOption} ${isCorrect ? styles.mcOption_correct : ''}`}>
                        <div className={styles.mcOptionHead}>
                            <div className={styles.mcOptionLabelWrap}>
                                <span className={styles.mcOptionText}>{label}</span>
                                {isCorrect && <span className={styles.mcBadgeCorrect}>Correct outcome</span>}
                            </div>
                        </div>
                        <div className={styles.mcOptionBarWrap}>
                            <div className={styles.mcBarTrack}>
                                <div
                                    className={`${styles.mcBarFill} ${styles.mcBarFill_default} ${isCorrect ? styles.mcBarFill_correct : ''}`}
                                    style={{ width: `${pct}%` }}
                                >
                                    {pct > 15 && <span className={styles.mcBarPct}>{pct}%</span>}
                                </div>
                            </div>
                            <span className={styles.mcBarCount}>{count}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ── Timeline Ordering ─────────────────────────────────────────────────────────
const TimelineOrderResults = ({ responses, content }) => {
    const correctOrder = content?.items || [];
    const scored = useMemo(() => responses.map(r => {
        const order = r.answer?.order || [];
        let correct = 0;
        correctOrder.forEach((item, i) => { if (order[i] === item) correct++; });
        return { participant: r.participant, correct, total: correctOrder.length };
    }).sort((a, b) => b.correct - a.correct), [responses, correctOrder]);

    const avg = scored.length ? scored.reduce((s, r) => s + r.correct, 0) / scored.length / Math.max(correctOrder.length, 1) * 100 : 0;

    return (
        <div className={styles.performanceBoard}>
            <div className={styles.avgBadge}>Avg accuracy: <strong>{avg.toFixed(0)}%</strong></div>
            {correctOrder.length > 0 && (
                <div style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>Correct order:</div>
                    {correctOrder.map((item, i) => (
                        <div key={item} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--text-sm)', padding: '2px 0' }}>
                            <span style={{ width: 20, height: 20, borderRadius: 4, background: 'color-mix(in srgb, var(--primary-500) 20%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                            <span style={{ color: 'var(--text-primary)' }}>{item}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className={styles.categoriesRows}>
                {scored.map((s, i) => {
                    const pct = Math.round((s.correct / Math.max(s.total, 1)) * 100);
                    return (
                        <div key={i} className={styles.categoriesRow}>
                            <span className={styles.categoriesName}>{s.participant}</span>
                            <div className={styles.categoriesMeter}>
                                <div className={styles.categoriesFill} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={styles.categoriesMeta}>{s.correct}/{s.total}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Hotspot ───────────────────────────────────────────────────────────────────
const DEFAULT_HOTSPOT_COLOR = '#0ea5e9';
const hexToRgbaHotspot = (hex, alpha) => {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return `rgba(14,165,233,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const HotspotResults = ({ responses, content }) => {
    const imageUrl = content?.settings?.imageUrl || '';
    const hotspots = content?.settings?.hotspots || [];
    const imageNaturalWidth = content?.settings?.imageNaturalWidth;
    const imageNaturalHeight = content?.settings?.imageNaturalHeight;
    const hasAspectRatio = imageNaturalWidth && imageNaturalHeight;
    const markerColor = content?.settings?.hotspotMarkerColor || DEFAULT_HOTSPOT_COLOR;
    const counts = useMemo(() => {
        const c = {};
        hotspots.forEach(h => { c[h.id] = 0; });
        responses.forEach(r => {
            const h = r.answer?.hotspot;
            if (h) c[h] = (c[h] || 0) + 1;
        });
        return c;
    }, [responses, hotspots]);
    const maxCount = Math.max(...Object.values(counts), 1);
    const total = Math.max(responses.length, 1);

    return (
        <div>
            <div className={styles.avgBadge} style={{ marginBottom: 'var(--space-3)' }}>{responses.length} response{responses.length !== 1 ? 's' : ''}</div>
            <div
                style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    marginBottom: 'var(--space-3)',
                    ...(hasAspectRatio ? { aspectRatio: `${imageNaturalWidth} / ${imageNaturalHeight}` } : {}),
                }}
            >
                <img
                    src={imageUrl}
                    alt="Hotspot results"
                    style={{
                        width: '100%',
                        height: hasAspectRatio ? '100%' : undefined,
                        display: 'block',
                        maxHeight: hasAspectRatio ? undefined : 360,
                        objectFit: 'contain',
                        background: 'var(--bg-tertiary)',
                    }}
                />
                {hotspots.map(hs => {
                    const count = counts[hs.id] || 0;
                    const intensity = count / maxCount;
                    const bgAlpha = 0.3 + intensity * 0.6;
                    const borderAlpha = 0.5 + intensity * 0.5;
                    return (
                        <div
                            key={hs.id}
                            style={{
                                position: 'absolute',
                                left: `${hs.x}%`,
                                top: `${hs.y}%`,
                                transform: 'translate(-50%,-50%)',
                                width: `${(hs.radius || 3) * 2}%`,
                                aspectRatio: '1',
                                borderRadius: '50%',
                                background: hexToRgbaHotspot(markerColor, bgAlpha),
                                border: `3px solid ${hexToRgbaHotspot(markerColor, borderAlpha)}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#1a1a1a',
                                fontWeight: 700,
                                fontSize: 12,
                                pointerEvents: 'none',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {count}
                        </div>
                    );
                })}
            </div>
            <div className={styles.mcChart}>
                {hotspots.map(hs => {
                    const count = counts[hs.id] || 0;
                    const pct = Math.round((count / total) * 100);
                    return (
                        <div key={hs.id} className={styles.mcOption}>
                            <div className={styles.mcOptionHead}><div className={styles.mcOptionLabelWrap}><span className={styles.mcOptionText}>{hs.label || hs.id}</span></div></div>
                            <div className={styles.mcOptionBarWrap}>
                                <div className={styles.mcBarTrack}><div className={`${styles.mcBarFill} ${styles.mcBarFill_default}`} style={{ width: `${pct}%` }}>{pct > 15 && <span className={styles.mcBarPct}>{pct}%</span>}</div></div>
                                <span className={styles.mcBarCount}>{count}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Debate ────────────────────────────────────────────────────────────────────
const DebateResults = ({ responses, content }) => {
    const [showArguments, setShowArguments] = useState(false);
    const agree = responses.filter(r => r.answer?.side === 'agree').length;
    const disagree = responses.filter(r => r.answer?.side === 'disagree').length;
    const total = Math.max(responses.length, 1);
    const agreeLabel = content?.settings?.agreeLabel || 'Agree';
    const disagreeLabel = content?.settings?.disagreeLabel || 'Disagree';

    const args = useMemo(() => responses.filter(r => r.answer?.argument).map(r => ({
        participant: r.participant,
        side: r.answer.side,
        argument: r.answer.argument,
    })), [responses]);

    const agreeWidth = Math.round((agree / total) * 100);
    const disagreeWidth = Math.round((disagree / total) * 100);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className={styles.avgBadge}>{responses.length} vote{responses.length !== 1 ? 's' : ''}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                {[{label: agreeLabel, count: agree, color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 10%, transparent)', width: agreeWidth, Icon: CheckCircle2}, {label: disagreeLabel, count: disagree, color: 'var(--error)', bg: 'color-mix(in srgb, var(--error) 10%, transparent)', width: disagreeWidth, Icon: XCircle}].map(item => (
                    <div key={item.label} style={{ padding: 'var(--space-4)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', background: item.bg, textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-1)', color: item.color }}>
                            <item.Icon size={32} aria-hidden />
                        </div>
                        <div style={{ fontWeight: 'var(--font-bold)', fontSize: 'var(--text-2xl)', color: item.color }}>{item.count}</div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{item.label}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 4 }}>{item.width}%</div>
                    </div>
                ))}
            </div>
            <div style={{ height: 8, borderRadius: 'var(--radius-full)', overflow: 'hidden', display: 'flex' }}>
                <div style={{ width: `${agreeWidth}%`, background: 'var(--success)', transition: 'width 0.5s ease' }} />
                <div style={{ flex: 1, background: 'var(--error)' }} />
            </div>
            {args.length > 0 && (
                <>
                    <button type="button" onClick={() => setShowArguments(s => !s)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', cursor: 'pointer', textAlign: 'left' }}>
                        {showArguments ? '▲' : '▼'} {args.length} argument{args.length !== 1 ? 's' : ''}
                    </button>
                    {showArguments && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {args.map((a, i) => (
                                <div key={i} style={{ padding: 'var(--space-3)', border: `1px solid ${a.side === 'agree' ? 'color-mix(in srgb, var(--success) 30%, transparent)' : 'color-mix(in srgb, var(--error) 30%, transparent)'}`, borderRadius: 'var(--radius-lg)', background: a.side === 'agree' ? 'color-mix(in srgb, var(--success) 5%, transparent)' : 'color-mix(in srgb, var(--error) 5%, transparent)' }}>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 4 }}>{a.participant} · {a.side === 'agree' ? agreeLabel : disagreeLabel}</div>
                                    <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>{a.argument}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ── Guess the Number ──────────────────────────────────────────────────────────
const GuessNumberResults = ({ responses, content }) => {
    const correctNumber = content?.settings?.correctNumber;
    const guesses = useMemo(() => {
        return responses.map(r => ({
            participant: r.participant,
            guess: r.answer?.guess,
            diff: correctNumber !== undefined ? Math.abs(r.answer?.guess - correctNumber) : null,
        })).sort((a, b) => {
            if (a.diff === null) return 0;
            return a.diff - b.diff;
        });
    }, [responses, correctNumber]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {correctNumber !== undefined && (
                <div
                    className={styles.avgBadge}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                        borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)',
                    }}
                >
                    <Target size={14} aria-hidden /> Correct number: <strong>{correctNumber}</strong>
                </div>
            )}
            <div className={styles.avgBadge}>{responses.length} guess{responses.length !== 1 ? 'es' : ''}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {guesses.map((g, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', background: i === 0 && correctNumber !== undefined ? 'color-mix(in srgb, var(--primary-500) 8%, transparent)' : 'var(--bg-tertiary)', border: `1px solid ${i === 0 && correctNumber !== undefined ? 'color-mix(in srgb, var(--primary-500) 30%, transparent)' : 'var(--border)'}`, borderRadius: 'var(--radius-lg)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 'var(--text-lg)' }}>{i === 0 && correctNumber !== undefined ? <Trophy size={16} className={styles.rankIconGold} aria-hidden /> : `#${i + 1}`}</span>
                        <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>{g.participant}</span>
                        <span style={{ fontWeight: 'var(--font-bold)', color: 'var(--primary-500)' }}>{g.guess}</span>
                        {g.diff !== null && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>off by {g.diff}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Speed Quiz ────────────────────────────────────────────────────────────────
const SpeedQuizResults = ({ responses, content }) => {
    const correctId = content?.settings?.correctAnswer;
    const options = content?.options || [];

    const results = useMemo(() => responses.map(r => ({
        participant: r.participant,
        answer: r.answer?.answer,
        responseTime: r.answer?.responseTime,
        isCorrect: correctId ? r.answer?.answer === correctId : null,
    })).sort((a, b) => {
        if (a.isCorrect !== b.isCorrect) return (b.isCorrect ? 1 : 0) - (a.isCorrect ? 1 : 0);
        return (a.responseTime || Infinity) - (b.responseTime || Infinity);
    }), [responses, correctId]);

    const correctCount = results.filter(r => r.isCorrect === true).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {correctId && (
                <div
                    className={styles.avgBadge}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                        borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)',
                    }}
                >
                    <CheckCircle2 size={14} aria-hidden /> Correct answer: <strong>{options.find(o => (o.id || o) === correctId)?.label || correctId}</strong>
                    <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>— {correctCount}/{responses.length} correct</span>
                </div>
            )}
            <div className={styles.leaderboard}>
                {results.map((r, i) => (
                    <div key={i} className={styles.leaderRow}>
                        <div className={styles.leaderLeft}>
                            <span className={styles.leaderRank}>
                                <RankIcon rank={i} size={16} />
                            </span>
                            <span className={styles.leaderName}>{r.participant}</span>
                            {r.isCorrect !== null && (
                                <span style={{ display: 'inline-flex', fontSize: 'var(--text-xs)', color: r.isCorrect ? 'var(--success)' : 'var(--error)' }}>
                                    {r.isCorrect ? <CheckCircle2 size={13} aria-label="Correct" /> : <XCircle size={13} aria-label="Incorrect" />}
                                </span>
                            )}
                        </div>
                        <div className={styles.leaderMeta}>
                            <span className={styles.leaderScore}>
                                {r.responseTime != null ? `${(r.responseTime / 1000).toFixed(2)}s` : '—'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Drawing ───────────────────────────────────────────────────────────────────
const DrawingResults = ({ responses }) => {
    const [zoomedResponse, setZoomedResponse] = useState(null);
    const drawings = responses.filter(r => r.answer?.drawingData);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div className={styles.avgBadge}>{responses.length} drawing{responses.length !== 1 ? 's' : ''} submitted</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--space-3)' }}>
                {drawings.map((r, i) => (
                    <button
                        key={r.id ?? i}
                        type="button"
                        onClick={() => setZoomedResponse(r)}
                        style={{
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-lg)',
                            overflow: 'hidden',
                            background: 'var(--bg-secondary)',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--primary-400)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}
                        title="Click to zoom"
                    >
                        <img src={r.answer.drawingData} alt={`Drawing by ${r.participant}`} style={{ width: '100%', display: 'block', background: '#fff', pointerEvents: 'none' }} />
                        <div style={{ padding: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textAlign: 'center' }}>
                            {r.participant}
                        </div>
                    </button>
                ))}
            </div>

            {zoomedResponse?.answer?.drawingData && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Zoomed drawing"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.7)',
                        padding: 'var(--space-4)',
                    }}
                    onClick={() => setZoomedResponse(null)}
                >
                    <div
                        style={{
                            position: 'relative',
                            maxWidth: '95vw',
                            maxHeight: '95vh',
                            background: 'var(--bg-primary)',
                            borderRadius: 'var(--radius-xl)',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3)', borderBottom: '1px solid var(--border)' }}>
                            <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text-primary)' }}>{zoomedResponse.participant}</span>
                            <button
                                type="button"
                                onClick={() => setZoomedResponse(null)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: 'var(--space-1) var(--space-2)',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius-md)',
                                    background: 'var(--bg-tertiary)',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-sm)',
                                }}
                                aria-label="Close"
                            >
                                <X size={14} aria-hidden /> Close
                            </button>
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                            <img
                                src={zoomedResponse.answer.drawingData}
                                alt={`Drawing by ${zoomedResponse.participant}`}
                                style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', background: '#fff', borderRadius: 'var(--radius-md)' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResultsView;

